// Identity bootstrap — the ONE place identity work is allowed to begin, and
// only ever called from the consent-accept path (see shell/App.tsx).
//
// CRITICAL (BUILD_PLAN.md §6 Phase 4 acceptance gate): no identifier of any
// kind — anonymous id, fingerprint, or device id — is generated, stored, or
// transmitted before this runs. `getAnonymousId()` (which generates+persists
// the local id) is therefore reachable only from here.

import type { ConsentRecord } from "../consent/state";
import { engagement } from "../feed/engagement";
import { clearInterestVector } from "../feed/interest";
import { bindIdentity, forgetIdentity } from "./api";
import { forgetAnonymousId, getAnonymousId } from "./anonymous";
import { clearDeviceId, getDeviceId, setDeviceId } from "./device";
import { getVisitorId } from "./fingerprint";

/**
 * Ensure the device is bound. Idempotent: returns the existing device id if
 * already bound, otherwise generates the local anonymous id, computes the
 * (optional) fingerprint, and binds via the Worker. Returns the device id, or
 * `null` if the bind couldn't complete (offline / Worker down) — the local id
 * is still established so a later attempt can succeed.
 */
export async function bootstrapIdentity(
  consent?: ConsentRecord | null,
): Promise<string | null> {
  const existing = await getDeviceId();
  if (existing) return existing;

  const anonymousId = await getAnonymousId();

  // Fingerprint is a secondary signal; failure to load must not block binding.
  let visitorId = "";
  try {
    visitorId = await getVisitorId();
  } catch {
    visitorId = "";
  }

  try {
    const deviceId = await bindIdentity(visitorId, anonymousId, consent);
    await setDeviceId(deviceId);
    return deviceId;
  } catch {
    return null;
  }
}

/**
 * "Forget me": ask the Worker to tombstone the device server-side, then wipe
 * all local identity + personalization state. Local cleanup runs even if the
 * server call fails so the device looks fresh on this browser immediately.
 */
export async function forgetMe(): Promise<void> {
  try {
    await forgetIdentity();
  } finally {
    engagement.reset();
    await Promise.all([
      clearDeviceId(),
      forgetAnonymousId(),
      clearInterestVector(),
    ]);
  }
}
